import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import {
    Alert,
    FormControl,
    IconButton,
    Link as MuiLink,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import MultiSelect from "../../components/multiSelect/multiSelect";
import {searchPubs} from "../blockedPublishers/blockedPublishersService";
import downloadCsv from "../../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";

type FeedbackRow = {
    id: number;
    date: string;
    publisherId: number;
    publisherName: string;
    rating: number;
    comment?: string | null;
    email?: string | null;
    url: string;
    ip?: string | null;
    userAgent?: string | null;
};

const PAGE_SIZE = 100;

const RATINGS = [
    {id: 1, value: "1"},
    {id: 2, value: "2"},
    {id: 3, value: "3"},
    {id: 4, value: "4"},
    {id: 5, value: "5"},
];

async function searchRatings(search: string, selected: any[]) {
    const selectedIds = selected.map((x) => x.id);
    return RATINGS.filter((x) =>
        !selectedIds.includes(x.id) && x.value.includes((search || "").trim())
    );
}

function pathDomain(url: string): string {
    if (!url) {
        return "";
    }
    try {
        return new URL(url).hostname || url;
    } catch {
        return url.replace(/^https?:\/\//i, "").split("/")[0] || url;
    }
}

export default function FeedbackPage() {
    React.useEffect(() => {
        document.title = "Feedback | DN";
    }, []);

    const [dateRange, setDateRange] = React.useState([] as any);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState([] as FeedbackRow[]);
    const [count, setCount] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as any[]);
    const [selectedRatings, setSelectedRatings] = React.useState([] as any[]);
    const [search, setSearch] = React.useState("");
    const [expanded, setExpanded] = React.useState({} as Record<number, boolean>);

    React.useEffect(() => {
        const d = RangeToTimes("Last 7 Days");
        setDateRange(d);
        setFiltersLoaded(true);
        reload(d, [], [], "", 0).then(() => {});
    }, []);

    const fetchFeedback = async (
        date = dateRange,
        pubs = selectedPublishers,
        ratings = selectedRatings,
        searchVal = search,
        pageNum = page,
        limit = PAGE_SIZE,
    ) => {
        const q = gql`
            query q($input: PathFeedbackListInput!) {
                pathFeedbackList(input: $input) {
                    count
                    items {
                        id
                        date
                        publisherId
                        publisherName
                        rating
                        comment
                        email
                        url
                        ip
                        userAgent
                    }
                }
            }
        `;
        const variables = {
            input: {
                startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
                endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
                publisherIds: pubs.map((x: any) => x.id),
                ratings: ratings.map((x: any) => x.id),
                search: searchVal.trim() || null,
                skip: pageNum * limit,
                limit,
            },
        };
        return query(q, variables);
    };

    const reload = async (
        date = dateRange,
        pubs = selectedPublishers,
        ratings = selectedRatings,
        searchVal = search,
        pageNum = page,
    ) => {
        if (!date || date.length < 2) {
            toast.error("Select a date range");
            return;
        }
        setLoading(true);
        const response = await fetchFeedback(date, pubs, ratings, searchVal, pageNum, PAGE_SIZE);
        const list = response && response.pathFeedbackList;
        setResults((list && list.items) || []);
        setCount((list && list.count) || 0);
        setExpanded({});
        setLoading(false);
    };

    const runSearch = async () => {
        setPage(0);
        await reload(dateRange, selectedPublishers, selectedRatings, search, 0);
    };

    const handlePageChange = async (_event: React.ChangeEvent<unknown>, value: number) => {
        const next = value - 1;
        setPage(next);
        await reload(dateRange, selectedPublishers, selectedRatings, search, next);
    };

    const download = async () => {
        if (!dateRange || dateRange.length < 2) {
            toast.error("Select a date range");
            return;
        }
        setLoading(true);
        const response = await fetchFeedback(
            dateRange,
            selectedPublishers,
            selectedRatings,
            search,
            0,
            10000,
        );
        setLoading(false);
        const items: FeedbackRow[] = (response && response.pathFeedbackList && response.pathFeedbackList.items) || [];
        const content: any[][] = [[
            "Date", "Publisher", "PublisherId", "Rating", "Comment", "Email", "Path", "URL", "IP", "User-Agent",
        ]];
        items.forEach((r) => {
            content.push([
                r.date,
                r.publisherName,
                r.publisherId,
                r.rating,
                r.comment || "",
                r.email || "",
                pathDomain(r.url),
                r.url,
                r.ip || "",
                r.userAgent || "",
            ]);
        });
        await downloadCsv("feedback.csv", content);
    };

    const maxPage = Math.max(1, Math.ceil(count / PAGE_SIZE));

    if (!filtersLoaded) {
        return <Loading/>;
    }

    return (
        <>
            <h2>Feedback</h2>
            <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 1}}>
                <DateRange dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: "10px"}}/>
                <FormControl variant="standard" sx={{width: "200px"}}>
                    <MultiSelect
                        searchFunction={searchPubs}
                        selectedItems={selectedPublishers}
                        setSelectedItems={setSelectedPublishers}
                        placeholder={"Publishers"}
                    />
                </FormControl>
                <FormControl variant="standard" sx={{width: "120px"}}>
                    <MultiSelect
                        searchFunction={searchRatings}
                        selectedItems={selectedRatings}
                        setSelectedItems={setSelectedRatings}
                        placeholder={"Rating"}
                    />
                </FormControl>
                <TextField
                    variant="standard"
                    label="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{width: "180px"}}
                    placeholder="email, comment, IP"
                />
                <Button
                    onClick={() => runSearch()}
                    variant="contained"
                    color="success"
                    disabled={loading}
                >
                    Run
                </Button>
                {count > 0 && (
                    <IconButton aria-label="download" onClick={download} title="Download CSV" disabled={loading}>
                        <FileDownloadIcon/>
                    </IconButton>
                )}
            </Box>
            <p></p>
            {loading ? <Loading/> :
                results.length === 0 ? <Alert severity="warning">No results found.</Alert> :
                    <>
                        <Box sx={{mb: 1}}>
                            Showing {1 + page * PAGE_SIZE} to {Math.min((page + 1) * PAGE_SIZE, count).toLocaleString()} of {count.toLocaleString()}.
                        </Box>
                        <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 10px)", maxHeight: "calc(100vh - 340px)"}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableHeaderRow>
                                        <TableCell sx={{width: 40}}/>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Publisher</TableCell>
                                        <TableCell>Path</TableCell>
                                        <TableCell>Rating</TableCell>
                                        <TableCell>Comment</TableCell>
                                        <TableCell>Email</TableCell>
                                    </TableHeaderRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((row) => (
                                        <React.Fragment key={row.id}>
                                            <StripedTableRow>
                                                <TableCell sx={{width: 40, paddingRight: 0}}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setExpanded({...expanded, [row.id]: !expanded[row.id]})}
                                                    >
                                                        {expanded[row.id]
                                                            ? <IndeterminateCheckBoxOutlinedIcon sx={{fontSize: 20}}/>
                                                            : <AddBoxOutlinedIcon sx={{fontSize: 20}}/>}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{whiteSpace: "nowrap"}}>
                                                    {row.date ? moment(row.date).format("YYYY-MM-DD HH:mm") : ""}
                                                </TableCell>
                                                <TableCell>
                                                    <MuiLink component={Link} to={`/publishers/${row.publisherId}`}>
                                                        {row.publisherName || "?"} ({row.publisherId})
                                                    </MuiLink>
                                                </TableCell>
                                                <TableCell sx={{whiteSpace: "nowrap"}}>
                                                    {pathDomain(row.url)}
                                                </TableCell>
                                                <TableCell>{row.rating}</TableCell>
                                                <TableCell sx={{maxWidth: 320, wordBreak: "break-word"}}>
                                                    {row.comment || ""}
                                                </TableCell>
                                                <TableCell>
                                                    {row.email ? (
                                                        <MuiLink
                                                            href={`/lead?email=${encodeURIComponent(row.email)}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {row.email}
                                                        </MuiLink>
                                                    ) : ""}
                                                </TableCell>
                                            </StripedTableRow>
                                            {!!expanded[row.id] && (
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{backgroundColor: "#fafafa"}}>
                                                        <Box sx={{display: "flex", flexDirection: "column", gap: 0.5, py: 0.5}}>
                                                            <Box>
                                                                <strong>URL:</strong>{" "}
                                                                {row.url ? (
                                                                    <MuiLink href={row.url} target="_blank" rel="noreferrer">
                                                                        {row.url}
                                                                    </MuiLink>
                                                                ) : ""}
                                                            </Box>
                                                            <Box>
                                                                <strong>IP:</strong> {row.ip || ""}
                                                            </Box>
                                                            <Box sx={{wordBreak: "break-word"}}>
                                                                <strong>User Agent:</strong> {row.userAgent || ""}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {maxPage > 1 && (
                            <Pagination
                                count={maxPage}
                                page={page + 1}
                                onChange={handlePageChange}
                                variant="outlined"
                                shape="rounded"
                                style={{paddingTop: 15}}
                            />
                        )}
                    </>
            }
        </>
    );
}
